import type { FrameworkActionList, FrameworkClass } from './framework.d';
import { FrameworkClassPartType, FrameworkClassType } from './framework-types';
import { HIGH_SPEED_MOTOR_MAX_RPM, HIGH_TORQUE_MOTOR_MAX_RPM } from '../utils/motorVelocityLimits';

export const FrameworkVersion = '1.3';
export const MinFirmwareVersion = 'v3.5.1546';

const HighSpeedMotor: FrameworkClass = {
  name: 'HighSpeedMotor',
  type: FrameworkClassType.Electronic,
  partType: FrameworkClassPartType.Motors,
  uiPanel: 'HighSpeedMotor',
  children: [],
  initialization: [
    {
      parameter: 'motor_port',
      type: 'integer',
      value: '1',
      constructor_arg: 'port',
      property: 'port',
    },
    {
      parameter: 'max_torque',
      type: 'float',
      value: '0.18',
      units: 'N·m',
      property: 'max_torque',
    },
    {
      parameter: 'motor_direction',
      type: 'MotorDirection',
      value: 'MotorDirection.FORWARD',
      property: 'motor_direction',
    },
    {
      parameter: 'stop_mode',
      type: 'MotorStopMode',
      value: 'MotorStopMode.BRAKE',
      property: 'stop_mode',
    },
  ],
  actions: [
    {
      name: 'set_position_action',
      arguments: [
        { name: 'position', type: 'float' },
        {
          name: 'units',
          type: 'RotationalPositionUnits',
          defaultValue: 'RotationalPositionUnits.DEGREES',
        },
      ],
    },
    {
      name: 'set_velocity_action',
      arguments: [
        { name: 'velocity', type: 'float', motorVelocityLimitRpm: HIGH_SPEED_MOTOR_MAX_RPM },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
      ],
    },
    {
      name: 'set_output_percent_action',
      arguments: [{ name: 'value', type: 'float', min: -100, max: 100, defaultValue: 0 }],
    },
    {
      name: 'reset_position_action',
      arguments: [
        { name: 'value', type: 'float', defaultValue: 0.0 },
        {
          name: 'units',
          type: 'RotationalPositionUnits',
          defaultValue: 'RotationalPositionUnits.DEGREES',
        },
      ],
    },
    { name: 'stop_action' },
    {
      name: 'print_to_screen_position_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'RotationalPositionUnits',
          defaultValue: 'RotationalPositionUnits.DEGREES',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_velocity_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_output_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_current_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_temperature_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  axisActions: [
    {
      name: 'add_variable_velocity_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        { name: 'velocity', type: 'float', motorVelocityLimitRpm: HIGH_SPEED_MOTOR_MAX_RPM },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
      ],
    },
    {
      name: 'add_variable_output_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'percent',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
  ],
  streamData: [
    { name: 'motor.port{port}.is_at_target', type: 'electronic_bool' },
    { name: 'motor.port{port}.velocity', type: 'electronic_float', min: -3200, max: 3200 },
    { name: 'motor.port{port}.position', type: 'electronic_float' },
    { name: 'motor.port{port}.acceleration', type: 'electronic_float', min: -10000, max: 10000 },
    { name: 'motor.port{port}.current', type: 'electronic_float', min: -2, max: 2 },
    { name: 'motor.port{port}.available_voltage', type: 'electronic_float', min: 10, max: 16.4 },
    { name: 'motor.port{port}.used_voltage', type: 'electronic_float', min: 0, max: 12 },
    { name: 'motor.port{port}.temperature', type: 'electronic_float' },
    { name: 'motor.port{port}.power_output', type: 'electronic_float', min: 0, max: 11 },
  ],
};

const HighTorqueMotor: FrameworkClass = {
  name: 'HighTorqueMotor',
  type: FrameworkClassType.Electronic,
  partType: FrameworkClassPartType.Motors,
  uiPanel: 'HighTorqueMotor',
  children: [],
  initialization: [
    {
      parameter: 'motor_port',
      type: 'integer',
      value: '1',
      constructor_arg: 'port',
      property: 'port',
    },
    {
      parameter: 'max_torque',
      type: 'float',
      value: '0.93',
      units: 'N·m',
      property: 'max_torque',
    },
    {
      parameter: 'motor_direction',
      type: 'MotorDirection',
      value: 'MotorDirection.FORWARD',
      property: 'motor_direction',
    },
    {
      parameter: 'stop_mode',
      type: 'MotorStopMode',
      value: 'MotorStopMode.BRAKE',
      property: 'stop_mode',
    },
  ],
  actions: [
    {
      name: 'set_position_action',
      arguments: [
        { name: 'position', type: 'float' },
        {
          name: 'units',
          type: 'RotationalPositionUnits',
          defaultValue: 'RotationalPositionUnits.DEGREES',
        },
      ],
    },
    {
      name: 'set_velocity_action',
      arguments: [
        { name: 'velocity', type: 'float', motorVelocityLimitRpm: HIGH_TORQUE_MOTOR_MAX_RPM },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
      ],
    },
    {
      name: 'set_output_percent_action',
      arguments: [{ name: 'value', type: 'float', min: -100, max: 100, defaultValue: 0 }],
    },
    {
      name: 'reset_position_action',
      arguments: [
        { name: 'value', type: 'float', defaultValue: 0.0 },
        {
          name: 'units',
          type: 'RotationalPositionUnits',
          defaultValue: 'RotationalPositionUnits.DEGREES',
        },
      ],
    },
    { name: 'stop_action' },
    {
      name: 'print_to_screen_position_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'RotationalPositionUnits',
          defaultValue: 'RotationalPositionUnits.DEGREES',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_velocity_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_output_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_current_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_temperature_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  axisActions: [
    {
      name: 'add_variable_velocity_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        { name: 'velocity', type: 'float', motorVelocityLimitRpm: HIGH_TORQUE_MOTOR_MAX_RPM },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
      ],
    },
    {
      name: 'add_variable_output_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'percent',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
  ],
  streamData: [
    { name: 'motor.port{port}.is_at_target', type: 'electronic_bool' },
    { name: 'motor.port{port}.velocity', type: 'electronic_float', min: -540, max: 540 },
    { name: 'motor.port{port}.position', type: 'electronic_float' },
    { name: 'motor.port{port}.acceleration', type: 'electronic_float', min: -2500, max: 2500 },
    { name: 'motor.port{port}.current', type: 'electronic_float', min: -2, max: 2 },
    { name: 'motor.port{port}.available_voltage', type: 'electronic_float', min: 10, max: 16.4 },
    { name: 'motor.port{port}.used_voltage', type: 'electronic_float', min: 0, max: 12 },
    { name: 'motor.port{port}.temperature', type: 'electronic_float' },
    { name: 'motor.port{port}.power_output', type: 'electronic_float', min: 0, max: 11 },
  ],
};

const BeamBreakSensor: FrameworkClass = {
  name: 'BeamBreakSensor',
  type: FrameworkClassType.Electronic,
  partType: FrameworkClassPartType.Sensors,
  uiPanel: 'BeamBreakSensor',
  children: [],
  initialization: [
    {
      parameter: 'sensor_port',
      type: 'integer',
      value: '1',
      constructor_arg: 'port',
      property: 'port',
    },
    {
      parameter: 'detection_range',
      type: 'float',
      value: '0.2',
      units: 'meters',
      constructor_arg: 'detection_range',
      property: 'detection_range',
    },
  ],
  actions: [
    {
      name: 'print_to_screen_is_beam_broken_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  streamData: [{ name: 'beam.port{port}.is_beam_broken', type: 'electronic_bool' }],
  sensorTriggers: [{ name: 'beam_broken_trigger' }],
};

const DistanceSensor: FrameworkClass = {
  name: 'DistanceSensor',
  type: FrameworkClassType.Electronic,
  partType: FrameworkClassPartType.Sensors,
  uiPanel: 'DistanceSensor',
  children: [],
  initialization: [
    {
      parameter: 'sensor_port',
      type: 'integer',
      value: '1',
      constructor_arg: 'port',
      property: 'port',
    },
  ],
  actions: [
    {
      name: 'print_to_screen_distance_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'LinearPositionUnits',
          defaultValue: 'LinearPositionUnits.METERS',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  streamData: [{ name: 'distance.port{port}.distance', type: 'electronic_float', min: 0, max: 1.5 }],
  sensorTriggers: [
    {
      name: 'greater_than_trigger',
      arguments: [{ name: 'distance', type: 'float', min: 0, max: 1, defaultValue: 0.5 }],
    },
    {
      name: 'less_than_trigger',
      arguments: [{ name: 'distance', type: 'float', min: 0, max: 1, defaultValue: 0.5 }],
    },
    {
      name: 'between_trigger',
      arguments: [
        { name: 'distance_near', type: 'float', min: 0, max: 1, defaultValue: 0.25 },
        { name: 'distance_far', type: 'float', min: 0, max: 1, defaultValue: 0.75 },
      ],
    },
  ],
};

const MagneticProximitySwitch: FrameworkClass = {
  name: 'MagneticProximitySwitch',
  type: FrameworkClassType.Electronic,
  partType: FrameworkClassPartType.Sensors,
  uiPanel: 'MagneticProximitySwitch',
  children: [],
  initialization: [
    {
      parameter: 'sensor_port',
      type: 'integer',
      value: '1',
      constructor_arg: 'port',
      property: 'port',
    },
  ],
  actions: [
    {
      name: 'print_to_screen_is_detected_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  streamData: [{ name: 'magnetic.port{port}.is_detected', type: 'electronic_bool' }],
  sensorTriggers: [{ name: 'detected_trigger' }],
};

const BitAdapter: FrameworkClass = {
  name: 'BitAdapter',
  type: FrameworkClassType.Electronic,
  partType: FrameworkClassPartType.Other,
  uiPanel: 'BitAdapter',
  children: [],
  initialization: [
    {
      parameter: 'motor_port',
      type: 'integer',
      value: '1',
      constructor_arg: 'port',
      property: 'port',
    },
  ],
  actions: [
    {
      name: 'set_output_percent_action',
      arguments: [{ name: 'percent', type: 'float', min: -100, max: 100 }],
    },
    {
      name: 'print_to_screen_output_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  axisActions: [
    {
      name: 'add_variable_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        { name: 'percent_at_min_input', type: 'float', min: -100, max: 100, defaultValue: 0.0 },
        { name: 'percent_at_max_input', type: 'float', min: -100, max: 100, defaultValue: 100.0 },
      ],
    },
  ],
  streamData: [{ name: 'bit_adapter.port{port}.output_percent', type: 'electronic_float' }],
};

const DriveBase: FrameworkClass = {
  name: 'DriveBase',
  type: FrameworkClassType.Mechanism,
  uiPanel: 'DriveBase',
  children: [],
  initialization: [
    {
      parameter: 'drive_type',
      type: 'enum',
      value: 'DriveType.SWERVE',
      constructor_arg: 'drive_type',
      property: 'drive_type',
    },
    {
      parameter: 'reference_frame',
      type: 'enum',
      value: 'ReferenceFrame.FIELD_CENTRIC',
      constructor_arg: 'reference_frame',
      property: 'reference_frame',
    },
    {
      parameter: 'control_stick_drive_style',
      type: 'enum',
      value: 'ControlStickDriveStyle.SWERVE_TWO_STICK',
      constructor_arg: 'control_stick_drive_style',
      property: 'control_stick_drive_style',
    },
    {
      parameter: 'wheel_diameter',
      type: 'int',
      value: '70',
      units: 'millimeters',
      property: 'wheel_diameter',
    },
    {
      parameter: 'max_translation_speed',
      type: 'float',
      value: '1.4',
      units: 'm/s',
      property: 'max_translation_speed',
    },
    {
      parameter: 'max_rotation_speed',
      type: 'float',
      value: '360.0',
      units: 'deg/sec',
      property: 'max_rotation_speed',
    },
    {
      parameter: 'debug_mode',
      type: 'bool',
      value: true,
      property: 'debug_mode',
    },
  ],
  actions: [
    {
      name: 'set_reference_frame_action',
      arguments: [{ name: 'mode', type: 'ReferenceFrame' }],
    },
    {
      name: 'drive_mode_action',
      arguments: [
        {
          name: 'mode',
          type: 'DriveMode',
          defaultValue: 'DriveMode.MANUAL',
        },
      ],
    },
    {
      name: 'drive_for_time_action',
      arguments: [
        {
          name: 'duration',
          type: 'float',
          min: 0,
          defaultValue: 2.0,
          units: 'seconds',
        },
        {
          name: 'heading',
          type: 'float',
          min: -180,
          max: 180,
          defaultValue: 0.0,
          units: 'degrees',
        },
        {
          name: 'velocity',
          type: 'float',
          min: 0,
          max: 1.4,
          defaultValue: 1.0,
          units: 'm/s',
        },
        {
          name: 'target_orientation',
          type: 'float',
          defaultValue: null,
          min: -180,
          max: 180,
          units: 'degrees',
        },
      ],
    },
    {
      name: 'drive_for_distance_action',
      arguments: [
        {
          name: 'distance',
          type: 'float',
          defaultValue: 1.0,
          units: 'meters',
        },
        {
          name: 'heading',
          type: 'float',
          min: -180,
          max: 180,
          defaultValue: 0.0,
          units: 'degrees',
        },
        {
          name: 'velocity',
          type: 'float',
          min: 0,
          max: 1.4,
          defaultValue: 1.0,
          units: 'm/s',
        },
        {
          name: 'target_orientation',
          type: 'float',
          defaultValue: null,
          min: -180,
          max: 180,
          units: 'degrees',
        },
      ],
    },
    {
      name: 'follow_path_action',
      arguments: [
        { name: 'path', type: 'path_name' },
        {
          name: 'coordinates_mode',
          type: 'PathCoordinatesMode',
          defaultValue: 'PathCoordinatesMode.ABSOLUTE',
        },
      ],
    },
    {
      name: 'set_leds_action',
      arguments: [
        { name: 'red', type: 'int', min: 0, max: 255, defaultValue: 100 },
        { name: 'green', type: 'int', min: 0, max: 255, defaultValue: 100 },
        { name: 'blue', type: 'int', min: 0, max: 255, defaultValue: 100 },
        {
          name: 'targets',
          type: 'SwerveLed',
          defaultValue: 'SwerveLed.ALL',
        },
      ],
    },
    {
      name: 'set_locator_action',
      arguments: [
        { name: 'x', type: 'float', units: 'meters' },
        { name: 'y', type: 'float', units: 'meters' },
        {
          name: 'yaw',
          type: 'float',
          min: -180,
          max: 180,
          units: 'degrees',
        },
      ],
    },
    { name: 'reset_locator_action' },
    { name: 'reset_orientation_action' },
  ],
  axisActions: [
    {
      name: 'add_variable_led_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'targets',
          type: 'SwerveLed',
          defaultValue: 'SwerveLed.ALL',
        },
        {
          name: 'color_at_min_input',
          type: 'tuple',
          members: [
            { name: 'red', type: 'int', min: 0, max: 255, defaultValue: 0 },
            { name: 'green', type: 'int', min: 0, max: 255, defaultValue: 0 },
            { name: 'blue', type: 'int', min: 0, max: 255, defaultValue: 0 },
          ],
        },
        {
          name: 'color_at_max_input',
          type: 'tuple',
          members: [
            { name: 'red', type: 'int', min: 0, max: 255, defaultValue: 100 },
            { name: 'green', type: 'int', min: 0, max: 255, defaultValue: 100 },
            { name: 'blue', type: 'int', min: 0, max: 255, defaultValue: 100 },
          ],
        },
      ],
    },
  ],
  streamData: [
    { name: 'Pitch', type: 'sensor_float', key: 'imu', min: -180, max: 180 },
    { name: 'Roll', type: 'sensor_float', key: 'imu', min: -180, max: 180 },
    { name: 'Yaw', type: 'sensor_float', key: 'imu', min: -180, max: 180 },
    { name: 'Accel X', type: 'sensor_float', key: 'accel' },
    { name: 'Accel Y', type: 'sensor_float', key: 'accel' },
    { name: 'Accel Z', type: 'sensor_float', key: 'accel' },
    { name: 'Gyro X', type: 'sensor_float', key: 'gyro' },
    { name: 'Gyro Y', type: 'sensor_float', key: 'gyro' },
    { name: 'Gyro Z', type: 'sensor_float', key: 'gyro' },
    { name: 'Position X', type: 'sensor_float', key: 'pos' },
    { name: 'Position Y', type: 'sensor_float', key: 'pos' },
    { name: 'Locator Yaw', type: 'sensor_float', key: 'locator' },
    { name: 'Linear Distance', type: 'sensor_float', key: 'dist' },
    { name: 'Velocity Magnitude', type: 'sensor_float', key: 'vel', min: 0, max: 1.4 },
    { name: 'Velocity X', type: 'sensor_float', key: 'vel', min: -1.4, max: 1.4 },
    { name: 'Velocity Y', type: 'sensor_float', key: 'vel', min: -1.4, max: 1.4 },
  ],
};

const RotationalMechanism: FrameworkClass = {
  name: 'RotationalMechanism',
  type: FrameworkClassType.Mechanism,
  uiPanel: 'RotationalMechanism',
  children: [{ class: HighTorqueMotor, property: 'motor' }],
  initialization: [
    {
      parameter: 'motor_port',
      type: 'int',
      value: '1',
      constructor_arg: 'motor_port',
      class: HighTorqueMotor,
    },
    {
      parameter: 'motor_type',
      type: 'MotorType',
      value: 'MotorType.HIGH_TORQUE',
      constructor_arg: 'motor_type',
      property: 'motor_type',
    },
    {
      parameter: 'motor_direction',
      type: 'MotorDirection',
      value: 'MotorDirection.FORWARD',
      constructor_arg: 'motor_direction',
      property: 'motor_direction',
    },
    {
      parameter: 'max_torque',
      type: 'float',
      value: null,
      defaultValueByParameter: {
        parameter: 'motor_type',
        values: {
          'MotorType.HIGH_SPEED': '0.18',
          'MotorType.HIGH_TORQUE': '0.93',
        },
      },
      units: 'N·m',
      property: 'motor_group.max_torque',
    },
    {
      parameter: 'stop_mode',
      type: 'MotorStopMode',
      value: 'MotorStopMode.BRAKE',
      property: 'motor_group.stop_mode',
    },
    {
      parameter: 'gear_ratio',
      type: 'float',
      value: '1.0',
      constructor_arg: 'gear_ratio',
      property: 'gear_ratio',
    },
    {
      parameter: 'min_position_limit',
      type: 'float',
      value: null,
      units: 'degrees',
      constructor_arg: 'min_position_limit',
      property: 'min_position_limit',
    },
    {
      parameter: 'max_position_limit',
      type: 'float',
      value: null,
      units: 'degrees',
      constructor_arg: 'max_position_limit',
      property: 'max_position_limit',
    },
    {
      parameter: 'min_velocity_limit',
      type: 'float',
      value: null,
      units: 'deg/sec',
      constructor_arg: 'min_velocity_limit',
      property: 'min_velocity_limit',
    },
    {
      parameter: 'max_velocity_limit',
      type: 'float',
      value: null,
      units: 'deg/sec',
      constructor_arg: 'max_velocity_limit',
      property: 'max_velocity_limit',
    },
    {
      parameter: 'enable_software_limits',
      type: 'bool',
      value: false,
      constructor_arg: 'enable_software_limits',
      property: 'enable_software_limits',
    },
    {
      parameter: 'enable_sensor_limits',
      type: 'bool',
      value: false,
      property: 'enable_sensor_limits',
    },
    {
      parameter: 'debug_mode',
      type: 'bool',
      value: false,
      property: 'debug_mode',
    },
  ],
  actions: [
    {
      name: 'set_position_action',
      arguments: [
        { name: 'position', type: 'float' },
        { name: 'units', type: 'RotationalPositionUnits' },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'set_position_percent_action',
      arguments: [{ name: 'percent', type: 'float', min: 0, max: 100 }],
    },
    {
      name: 'set_velocity_action',
      arguments: [
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'set_velocity_percent_action',
      arguments: [{ name: 'percent', type: 'float', min: -100, max: 100 }],
    },
    {
      name: 'set_output_percent_action',
      arguments: [
        {
          name: 'value',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
    {
      name: 'reset_position_action',
      arguments: [
        { name: 'position', type: 'float', defaultValue: 0.0 },
        { name: 'units', type: 'RotationalPositionUnits' },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'auto_zero_action',
      arguments: [
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'RotationalPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: -15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
      ],
    },
    {
      name: 'auto_zero_with_sensor_action',
      arguments: [
        { name: 'sensor', type: 'BeamBreakSensor | DistanceSensor | MagneticProximitySwitch' },
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'RotationalPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: -15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
        { name: 'invert_sensor', type: 'bool', defaultValue: false },
        { name: 'distance_criteria', type: 'float', min: 0, max: 1, defaultValue: 0.2 },
        { name: 'is_less_than', type: 'bool', defaultValue: true },
      ],
    },
    {
      name: 'auto_zero_with_min_sensor_action',
      arguments: [
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'RotationalPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: -15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
      ],
    },
    {
      name: 'auto_zero_with_max_sensor_action',
      arguments: [
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'RotationalPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: 15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
      ],
    },
    {
      name: 'stop_action',
      arguments: [
        {
          name: 'mode',
          type: 'MotorStopMode',
          defaultValue: 'MotorStopMode.BRAKE',
        },
      ],
    },
    {
      name: 'on_until_beam_true_action',
      arguments: [
        { name: 'beam_break_sensor', type: 'BeamBreakSensor' },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'time_delay', type: 'float', min: 0, defaultValue: 0 },
      ],
    },
    {
      name: 'on_until_distance_is_action',
      arguments: [
        { name: 'distance_sensor', type: 'DistanceSensor' },
        { name: 'distance_criteria', type: 'float', min: 0, max: 1, defaultValue: 0.2 },
        { name: 'is_less_than', type: 'bool', defaultValue: true },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'time_delay', type: 'float', min: 0, defaultValue: 0 },
      ],
    },
    {
      name: 'on_until_magnet_is_detected_action',
      arguments: [
        { name: 'magnetic_proximity_switch', type: 'MagneticProximitySwitch' },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'time_delay', type: 'float', min: 0, defaultValue: 0 },
      ],
    },
    {
      name: 'print_to_screen_position_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        { name: 'units', type: 'RotationalPositionUnits' },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_velocity_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_position_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_velocity_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_output_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  axisActions: [
    {
      name: 'add_variable_velocity_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'RotationalVelocityUnits',
          defaultValue: 'RotationalVelocityUnits.ROTATIONS_PER_MINUTE',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'add_variable_velocity_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'percent',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
    {
      name: 'add_variable_output_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'percent',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
  ],
  streamData: [
    { name: 'mechanism.port{motor_port}.velocity', type: 'electronic_float' },
    { name: 'mechanism.port{motor_port}.position', type: 'electronic_float' },
  ],
};

const LinearMechanism: FrameworkClass = {
  name: 'LinearMechanism',
  type: FrameworkClassType.Mechanism,
  uiPanel: 'RotationalMechanism',
  children: [{ class: HighTorqueMotor, property: 'motor' }],
  initialization: [
    {
      parameter: 'motor_port',
      type: 'int',
      value: '1',
      constructor_arg: 'motor_port',
      class: HighTorqueMotor,
    },
    {
      parameter: 'motor_type',
      type: 'MotorType',
      value: 'MotorType.HIGH_TORQUE',
      constructor_arg: 'motor_type',
      property: 'motor_type',
    },
    {
      parameter: 'motor_direction',
      type: 'MotorDirection',
      value: 'MotorDirection.FORWARD',
      constructor_arg: 'motor_direction',
      property: 'motor_direction',
    },
    {
      parameter: 'max_torque',
      type: 'float',
      value: null,
      defaultValueByParameter: {
        parameter: 'motor_type',
        values: {
          'MotorType.HIGH_SPEED': '0.18',
          'MotorType.HIGH_TORQUE': '0.93',
        },
      },
      units: 'N·m',
      property: 'motor_group.max_torque',
    },
    {
      parameter: 'stop_mode',
      type: 'MotorStopMode',
      value: 'MotorStopMode.BRAKE',
      property: 'motor_group.stop_mode',
    },
    {
      parameter: 'gear_ratio',
      type: 'float',
      value: '1.0',
      constructor_arg: 'gear_ratio',
      property: 'gear_ratio',
    },
    {
      parameter: 'distance_per_output_rotation',
      type: 'float',
      value: '1.0',
      units: 'meters',
      constructor_arg: 'distance_per_output_rotation',
      property: 'distance_per_output_rotation',
    },
    {
      parameter: 'min_position_limit',
      type: 'float',
      value: null,
      units: 'meters',
      constructor_arg: 'min_position_limit',
      property: 'min_position_limit',
    },
    {
      parameter: 'max_position_limit',
      type: 'float',
      value: null,
      units: 'meters',
      constructor_arg: 'max_position_limit',
      property: 'max_position_limit',
    },
    {
      parameter: 'min_velocity_limit',
      type: 'float',
      value: null,
      units: 'm/s',
      constructor_arg: 'min_velocity_limit',
      property: 'min_velocity_limit',
    },
    {
      parameter: 'max_velocity_limit',
      type: 'float',
      value: null,
      units: 'm/s',
      constructor_arg: 'max_velocity_limit',
      property: 'max_velocity_limit',
    },
    {
      parameter: 'enable_software_limits',
      type: 'bool',
      value: false,
      constructor_arg: 'enable_software_limits',
      property: 'enable_software_limits',
    },
    {
      parameter: 'enable_sensor_limits',
      type: 'bool',
      value: false,
      property: 'enable_sensor_limits',
    },
    {
      parameter: 'debug_mode',
      type: 'bool',
      value: false,
      property: 'debug_mode',
    },
  ],
  actions: [
    {
      name: 'set_position_action',
      arguments: [
        { name: 'position', type: 'float' },
        { name: 'units', type: 'LinearPositionUnits' },
      ],
    },
    {
      name: 'set_position_percent_action',
      arguments: [{ name: 'percent', type: 'float', min: 0, max: 100 }],
    },
    {
      name: 'set_velocity_action',
      sourceAwareLinearVelocity: true,
      arguments: [
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'LinearVelocityUnits',
          defaultValue: 'LinearVelocityUnits.METERS_PER_SECOND',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'set_velocity_percent_action',
      arguments: [{ name: 'percent', type: 'float', min: -100, max: 100 }],
    },
    {
      name: 'set_output_percent_action',
      arguments: [
        {
          name: 'value',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
    {
      name: 'reset_position_action',
      arguments: [
        { name: 'position', type: 'float', defaultValue: 0.0 },
        { name: 'units', type: 'LinearPositionUnits' },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'auto_zero_action',
      arguments: [
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'LinearPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: -15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
      ],
    },
    {
      name: 'auto_zero_with_sensor_action',
      arguments: [
        { name: 'sensor', type: 'BeamBreakSensor | DistanceSensor | MagneticProximitySwitch' },
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'LinearPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: -15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
        { name: 'invert_sensor', type: 'bool', defaultValue: false },
        { name: 'distance_criteria', type: 'float', min: 0, max: 1, defaultValue: 0.2 },
        { name: 'is_less_than', type: 'bool', defaultValue: true },
      ],
    },
    {
      name: 'auto_zero_with_min_sensor_action',
      arguments: [
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'LinearPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: -15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
      ],
    },
    {
      name: 'auto_zero_with_max_sensor_action',
      arguments: [
        { name: 'reset_to', type: 'float', defaultValue: 0.0 },
        {
          name: 'reset_units',
          type: 'LinearPositionUnits',
          defaultValue: null,
        },
        {
          name: 'reset_source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'seek_velocity_percent', type: 'float', defaultValue: 15.0 },
        { name: 'timeout_ms', type: 'int', defaultValue: 5000 },
      ],
    },
    {
      name: 'stop_action',
      arguments: [
        {
          name: 'mode',
          type: 'MotorStopMode',
          defaultValue: 'MotorStopMode.BRAKE',
        },
      ],
    },
    {
      name: 'on_until_beam_true_action',
      sourceAwareLinearVelocity: true,
      arguments: [
        { name: 'beam_break_sensor', type: 'BeamBreakSensor' },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'LinearVelocityUnits',
          defaultValue: 'LinearVelocityUnits.METERS_PER_SECOND',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'time_delay', type: 'float', min: 0, defaultValue: 0 },
      ],
    },
    {
      name: 'on_until_distance_is_action',
      sourceAwareLinearVelocity: true,
      arguments: [
        { name: 'distance_sensor', type: 'DistanceSensor' },
        { name: 'distance_criteria', type: 'float', min: 0, max: 1, defaultValue: 0.2 },
        { name: 'is_less_than', type: 'bool', defaultValue: true },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'LinearVelocityUnits',
          defaultValue: 'LinearVelocityUnits.METERS_PER_SECOND',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'time_delay', type: 'float', min: 0, defaultValue: 0 },
      ],
    },
    {
      name: 'on_until_magnet_is_detected_action',
      sourceAwareLinearVelocity: true,
      arguments: [
        { name: 'magnetic_proximity_switch', type: 'MagneticProximitySwitch' },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'LinearVelocityUnits',
          defaultValue: 'LinearVelocityUnits.METERS_PER_SECOND',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        { name: 'time_delay', type: 'float', min: 0, defaultValue: 0 },
      ],
    },
    {
      name: 'print_to_screen_position_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        { name: 'units', type: 'LinearPositionUnits' },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_velocity_live_action',
      sourceAwareLinearVelocity: true,
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'units',
          type: 'LinearVelocityUnits',
          defaultValue: 'LinearVelocityUnits.METERS_PER_SECOND',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_position_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_velocity_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_output_percent_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'prefix_text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
  ],
  axisActions: [
    {
      name: 'add_variable_velocity_control',
      sourceAwareLinearVelocity: true,
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        { name: 'velocity', type: 'float' },
        {
          name: 'units',
          type: 'LinearVelocityUnits',
          defaultValue: 'LinearVelocityUnits.METERS_PER_SECOND',
        },
        {
          name: 'source',
          type: 'MeasuredAt',
          defaultValue: 'MeasuredAt.MECHANISM_OUTPUT',
        },
      ],
    },
    {
      name: 'add_variable_velocity_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'percent',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
    {
      name: 'add_variable_output_percent_control',
      arguments: [
        { name: 'variable_input', type: 'Callable[[], float]' },
        {
          name: 'percent',
          type: 'float',
          min: -100,
          max: 100,
          defaultValue: 100,
        },
      ],
    },
  ],
  streamData: [
    { name: 'mechanism.port{motor_port}.velocity', type: 'electronic_float' },
    { name: 'mechanism.port{motor_port}.position', type: 'electronic_float' },
  ],
};

export const UtilityActions: FrameworkClass = {
  name: 'UtilityActions',
  type: FrameworkClassType.Utility,
  uiPanel: null,
  children: [],
  initialization: [],
  actions: [
    {
      name: 'print_to_screen_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'text', type: 'string' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
      ],
    },
    {
      name: 'print_to_screen_lines_action',
      arguments: [
        {
          name: 'lines',
          type: 'Sequence[tuple[int, str]]',
          members: [
            { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
            { name: 'text', type: 'string' },
          ],
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
      ],
    },
    {
      name: 'print_to_screen_live_action',
      arguments: [
        { name: 'line_number', type: 'int', min: 1, max: 7, defaultValue: 1 },
        { name: 'text_supplier', type: 'Callable[[], str]' },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'print_to_screen_lines_live_action',
      arguments: [
        {
          name: 'lines_supplier',
          type: 'Callable[[], Sequence[tuple[int, str]]]',
        },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
        { name: 'update_interval_ms', type: 'int', min: 0, defaultValue: 100 },
      ],
    },
    {
      name: 'set_background_action',
      arguments: [
        { name: 'red', type: 'int', min: 0, max: 255, defaultValue: 0 },
        { name: 'green', type: 'int', min: 0, max: 255, defaultValue: 0 },
        { name: 'blue', type: 'int', min: 0, max: 255, defaultValue: 0 },
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
      ],
    },
    {
      name: 'clear_screen_action',
      arguments: [
        {
          name: 'screen',
          type: 'PrintScreen',
          defaultValue: 'PrintScreen.CONTROLLER',
        },
      ],
    },
    { name: 'PrintAction', standalone: true, arguments: [{ name: 'message', type: 'string' }] },
    { name: 'WaitAction', standalone: true, groupOnly: true, arguments: [{ name: 'seconds', type: 'float', min: 0, defaultValue: 1 }] },
  ],
};

const FrameworkGroupActions: FrameworkActionList = {
  name: 'FrameworkGroupActions',
  actions: [
    { name: 'SequentialActionGroup', type: 'group' },
    { name: 'ParallelActionGroup', type: 'group' },
  ],
};

const FrameworkActions: FrameworkActionList = {
  name: 'FrameworkActions',
  actions: (UtilityActions.actions ?? []).filter((action) => action.standalone),
};

export const FrameworkClasses: FrameworkClass[] = [
  HighSpeedMotor,
  HighTorqueMotor,
  BeamBreakSensor,
  DistanceSensor,
  MagneticProximitySwitch,
  BitAdapter,
  DriveBase,
  RotationalMechanism,
  LinearMechanism,
];

export const FrameworkActionLists: FrameworkActionList[] = [FrameworkGroupActions, FrameworkActions];
